/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached } from '@glimmer/tracking';
import { type Registry as Services, service } from '@ember/service';
import PhClock from 'ember-phosphor-icons/components/ph-clock';
import PhCheckCircle from 'ember-phosphor-icons/components/ph-check-circle';
import PhXCircle from 'ember-phosphor-icons/components/ph-x-circle';
import { PaymentStatus } from 'uplisting-frontend/models/payment';
import type { ModelRegistry } from 'ember-data/types/registries/model';

interface IArgs {
  payment: ModelRegistry['payment'];
  index: number;
  count: number;
}

export interface PaymentStatusSignature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiPaymentStatusComponent extends Component<PaymentStatusSignature> {
  @service intl!: Services['intl'];

  @cached
  get payment(): ModelRegistry['payment'] {
    return this.args.payment;
  }

  @cached
  get status(): PaymentStatus {
    return this.payment.status;
  }

  @cached
  get alertClass():
    | 'inline-alert-danger'
    | 'inline-alert-processing'
    | 'inline-alert-success' {
    // TODO: define/confirm actual classes for the PaymentStatus.refunded & PaymentStatus.authorized
    // as they are not used/defined in the old app, but status can have that value
    switch (this.status) {
      case PaymentStatus.pending:
        return 'inline-alert-processing';
      case PaymentStatus.failing:
      case PaymentStatus.failed:
        return 'inline-alert-danger';
      case PaymentStatus.charged:
      case PaymentStatus.markedAsPaid:
      case PaymentStatus.refunded:
      case PaymentStatus.authorized:
        return 'inline-alert-success';
    }
  }

  @cached
  get iconComponent():
    | typeof PhClock
    | typeof PhCheckCircle
    | typeof PhXCircle
    | undefined {
    switch (this.status) {
      case PaymentStatus.pending:
        return PhClock;
      case PaymentStatus.failing:
      case PaymentStatus.failed:
        return PhXCircle;
      case PaymentStatus.charged:
      case PaymentStatus.markedAsPaid:
      case PaymentStatus.refunded:
      case PaymentStatus.authorized:
        return PhCheckCircle;
    }
  }

  @cached
  get sequence(): string {
    if (this.args.count === 1) {
      return '';
    }

    return 'I'.repeat(this.args.index + 1);
  }

  @cached
  get lastErrorMessage(): string {
    return (
      this.payment.lastErrorMessage ??
      this.intl.t('action_bookings_summary.payment_status.default_reason')
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Payment::Status': typeof UiPaymentStatusComponent;
  }
}
